import React, { useEffect, useState } from 'react';
import { 
    Row, Col, Card, Typography, Space, Table, Tag, 
    Descriptions, Badge, Avatar, Divider, Empty, Spin, 
    notification, Button, Tooltip, List 
} from 'antd';
import { 
    UserOutlined, 
    IdcardOutlined, 
    PhoneOutlined, 
    HomeOutlined, 
    HistoryOutlined, 
    FileTextOutlined, 
    DollarCircleOutlined,
    CalendarOutlined,
    SafetyCertificateOutlined,
    BarChartOutlined,
    TeamOutlined,
    HeartOutlined
} from '@ant-design/icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import axiosClient from '../../api/axiosClient';

// Register ChartJS modules
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartTitle,
  ChartTooltip,
  Legend
);

const { Title, Text, Paragraph } = Typography;

const MemberDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [metrics, setMetrics] = useState([]);
    const [relationships, setRelationships] = useState([]);
    const [assignedTrainers, setAssignedTrainers] = useState([]);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const [profileRes, invoiceRes, metricsRes] = await Promise.all([
                axiosClient.get('/portal/get_profile.php'),
                axiosClient.get('/portal/get_invoices.php'),
                axiosClient.get('/portal/get_metrics.php')
            ]);

            if (profileRes.data.status === 'success') setProfile(profileRes.data.data);
            if (invoiceRes.data.status === 'success') setInvoices(invoiceRes.data.data);
            if (metricsRes.data.status === 'success') {
                const allMetrics = metricsRes.data.data;
                setMetrics(allMetrics.slice(-7));
            }

            // Fetch relationships separately — uses /member/ auth which may differ
            try {
                const memberId = localStorage.getItem('USERID');
                const relRes = await axiosClient.get(`/member/get_relationships.php?member_id=${memberId}`);
                if (relRes.data.status === 'success') setRelationships(relRes.data.data);
            } catch (relError) {
                console.warn("Relationships fetch failed (non-critical):", relError);
            }

            // Fetch assigned trainers
            try {
                const trainerRes = await axiosClient.get('/portal/get_assigned_trainers.php');
                if (trainerRes.data.status === 'success') setAssignedTrainers(trainerRes.data.data || []);
            } catch (trainerError) {
                console.warn("Assigned trainers fetch failed (non-critical):", trainerError);
            }
            
        } catch (error) {
            console.error("Dashboard error:", error);
            notification.error({ message: "Data Fetch Error", description: "Failed to load dashboard statistics." });
        } finally {
            setLoading(false);
        }
    };

    const getStatusTag = (status) => {
        const colors = {
            'Paid': 'success',
            'Completed': 'success',
            'Partial': 'processing',
            'Pending': 'warning',
            'Unpaid': 'error',
            'Cancelled': 'default'
        };
        return <Tag color={colors[status] || 'default'} style={{ borderRadius: 6 }}>{status.toUpperCase()}</Tag>;
    };

    const invoiceColumns = [
        { title: 'Ref #', dataIndex: 'inv_ref', key: 'inv_ref', render: (text) => <Text strong>{text}</Text> },
        { title: 'Date', dataIndex: 'process_dt', key: 'process_dt', render: (date) => new Date(date).toLocaleDateString(), responsive: ['md'] },
        { title: 'Total', dataIndex: 'invoice_total', key: 'invoice_total', render: (val, record) => `${record.invoice_currency} ${parseFloat(val).toLocaleString()}`, responsive: ['sm'] },
        { title: 'Paid', dataIndex: 'total_paid_amount', key: 'total_paid_amount', render: (val, record) => <Text type="success">{record.invoice_currency} {parseFloat(val).toLocaleString()}</Text>, responsive: ['lg'] },
        { title: 'Balance', key: 'balance', render: (_, r) => <Text type="danger">{r.invoice_currency} {Math.max(0, parseFloat(r.invoice_total) - parseFloat(r.total_paid_amount)).toLocaleString()}</Text> },
        { title: 'Status', dataIndex: 'inv_status', key: 'inv_status', render: (status) => getStatusTag(status) },
    ];

    const paymentColumns = [
        { title: 'Payment Ref', dataIndex: 'payment_ref', key: 'payment_ref', render: (text) => text || 'N/A' },
        { title: 'Type', dataIndex: 'payment_type', key: 'payment_type' },
        { title: 'Amount', dataIndex: 'paid_amount', key: 'paid_amount', render: (val) => `${parseFloat(val).toLocaleString()}` },
        { title: 'Date', dataIndex: 'process_dt', key: 'process_dt', render: (date) => new Date(date).toLocaleString() },
    ];

    // Chart Data preparation
    const bodyCompData = {
        labels: metrics.map(m => m.log_date),
        datasets: [
            {
                label: 'Body Fat (%)',
                data: metrics.map(m => m.body_fat),
                backgroundColor: 'rgba(255, 99, 132, 0.7)',
                borderRadius: 8,
            },
            {
                label: 'Muscle Mass (%)',
                data: metrics.map(m => m.muscle_mass),
                backgroundColor: 'rgba(32, 156, 150, 0.7)',
                borderRadius: 8,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top', labels: { font: { family: 'Inter', size: 12 } } },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#333',
                bodyColor: '#666',
                borderColor: '#eee',
                borderWidth: 1,
                padding: 10,
                cornerRadius: 10,
                titleFont: { weight: 'bold' }
            }
        },
        scales: {
            y: { grid: { borderDash: [5, 5], color: 'rgba(0,0,0,0.02)' } },
            x: { grid: { display: false } },
        },
    };

    const glassStyle = {
        borderRadius: 20, 
        border: 'none', 
        boxShadow: '0 8px 32px rgba(0,0,0,0.05)', 
        background: 'rgba(255, 255, 255, 0.8)', 
        backdropFilter: 'blur(10px)'
    };

    return (
        <Spin spinning={loading} tip="Loading your dashboard...">
            <div style={{ width: '100%', paddingBottom: 40 }}>
                <Title level={2} style={{ color: '#1a1a1a', fontWeight: 800, marginBottom: 8, fontFamily: "'Inter', sans-serif" }}>
                    Dashboard 👤
                </Title>
                <p style={{ color: '#666', fontSize: 16, marginBottom: 32 }}>
                    Welcome back! Here's a quick overview of your membership and physical progress.
                </p>
                
                {/* Summary Section */}
                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={8}>
                        <Card style={{ ...glassStyle, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
                            <div style={{ textAlign: 'center', marginBottom: 24 }}>
                                <Avatar size={100} icon={<UserOutlined />} style={{ backgroundColor: '#209c96', marginBottom: 16 }} />
                                <Title level={3} style={{ margin: 0 }}>{profile?.name}</Title>
                                <Tag color="link" style={{ marginTop: 8 }}>MEMBER ID: {profile?.member_id}</Tag>
                            </div>
                            <Divider />
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text type="secondary"><Space size={4}><CalendarOutlined /> Birthday</Space></Text>
                                    <Text strong>{profile?.birthday ? new Date(profile.birthday).toLocaleDateString() : 'N/A'}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text type="secondary"><Space size={4}><SafetyCertificateOutlined /> Status</Space></Text>
                                    <Badge status={profile?.status ? "success" : "error"} text={profile?.status ? "Active" : "Inactive"} />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text type="secondary"><Space size={4}><HistoryOutlined /> Active Till</Space></Text>
                                    <Text strong style={{ color: '#fa8c16' }}>{profile?.active_till || 'N/A'}</Text>
                                </div>
                            </Space>
                        </Card>
                    </Col>

                    <Col xs={24} lg={16}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            {/* Personal details info cards */}
                            <Row gutter={[16,16]}>
                                <Col xs={24} sm={12}>
                                    <Card size="small" style={{ borderRadius: 15 }}>
                                        <Space direction="vertical">
                                            <Text type="secondary">T-Shirt Size</Text>
                                            <Title level={4} style={{ margin: 0 }}>{profile?.tshirt_size || 'N/A'}</Title>
                                        </Space>
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Card size="small" style={{ borderRadius: 15 }}>
                                        <Space direction="vertical">
                                            <Text type="secondary">Shoe Size</Text>
                                            <Title level={4} style={{ margin: 0 }}>{profile?.shoe_size || 'N/A'}</Title>
                                        </Space>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Detailed Profile */}
                            <Card title={<Space><IdcardOutlined /> Personal Info</Space>} style={glassStyle}>
                                <Descriptions column={{ xs: 1, sm: 2 }} bordered={false}>
                                    <Descriptions.Item label="NIC">{profile?.nic}</Descriptions.Item>
                                    <Descriptions.Item label="Contact">
                                        <Space size={8}><PhoneOutlined style={{ color: '#bfbfbf' }} /> {profile?.contact_no}</Space>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Emergency">
                                        <Space size={8}><PhoneOutlined style={{ color: '#bfbfbf' }} /> {profile?.emergency_contact}</Space>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Address" span={2}>
                                        <Space align="start" size={8}><HomeOutlined style={{ color: '#bfbfbf', marginTop: 4 }} /> {profile?.address}</Space>
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>

                            {/* Family & Relationships */}
                            <Card 
                                title={<Space><TeamOutlined /> Family & Relationships</Space>} 
                                style={glassStyle}
                            >
                                {relationships.length > 0 ? (
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={relationships}
                                        renderItem={(item) => (
                                            <List.Item>
                                                <List.Item.Meta
                                                    avatar={
                                                        <Avatar 
                                                            style={{ 
                                                                backgroundColor: '#f0fcfb', 
                                                                color: '#209c96',
                                                                border: '1px solid #d6f5f3'
                                                            }} 
                                                            icon={<HeartOutlined />} 
                                                        />
                                                    }
                                                    title={
                                                        <Space>
                                                            <Text strong>{item.name}</Text>
                                                            <Tag color="cyan" style={{ borderRadius: 8 }}>{item.relation_type}</Tag>
                                                        </Space>
                                                    }
                                                    description={
                                                        <Space split={<Divider type="vertical" />}>
                                                            <Text type="secondary" style={{ fontSize: 12 }}>ID: {item.member_id}</Text>
                                                            {item.contact_no && <Text type="secondary" style={{ fontSize: 12 }}><PhoneOutlined /> {item.contact_no}</Text>}
                                                        </Space>
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                ) : (
                                    <Empty description="No linked family members found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                )}
                            </Card>

                            {/* Assigned Trainers */}
                            <Card 
                                title={<Space><TeamOutlined style={{ color: '#722ed1' }} /> My Assigned Trainers</Space>} 
                                style={glassStyle}
                            >
                                {assignedTrainers.length > 0 ? (
                                    <Row gutter={[16, 16]}>
                                        {assignedTrainers.map((trainer) => (
                                            <Col xs={24} sm={12} key={trainer.id}>
                                                <Card 
                                                    size="small" 
                                                    style={{ 
                                                        borderRadius: 16, 
                                                        border: '1px solid #f0f0f0',
                                                        background: 'linear-gradient(135deg, #f8f9ff 0%, #f0fcfb 100%)'
                                                    }}
                                                >
                                                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                        <Space>
                                                            <Avatar 
                                                                style={{ 
                                                                    backgroundColor: '#722ed1', 
                                                                    verticalAlign: 'middle' 
                                                                }} 
                                                                size="large"
                                                            >
                                                                {trainer.name?.charAt(0)?.toUpperCase()}
                                                            </Avatar>
                                                            <div>
                                                                <Text strong style={{ fontSize: 15 }}>{trainer.name}</Text>
                                                                <br />
                                                                <Text type="secondary" style={{ fontSize: 12 }}>ID: {trainer.trainer_id}</Text>
                                                            </div>
                                                        </Space>
                                                        <Divider style={{ margin: '8px 0' }} />
                                                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                                            {trainer.specialization && (
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <Text type="secondary" style={{ fontSize: 12 }}>Specialization</Text>
                                                                    <Tag color="purple" style={{ borderRadius: 8, margin: 0 }}>{trainer.specialization}</Tag>
                                                                </div>
                                                            )}
                                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <Text type="secondary" style={{ fontSize: 12 }}><PhoneOutlined /> Contact</Text>
                                                                <Text style={{ fontSize: 12 }}>{trainer.contact_no || 'N/A'}</Text>
                                                            </div>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                                                                <Badge 
                                                                    status={trainer.status ? 'success' : 'error'} 
                                                                    text={<Text style={{ fontSize: 12 }}>{trainer.status ? 'Active' : 'Inactive'}</Text>}
                                                                />
                                                            </div>
                                                        </Space>
                                                    </Space>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                ) : (
                                    <Empty description="No trainers assigned yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                )}
                            </Card>

                            {/* Recent Progress Chart */}
                            <Card title={<Space><BarChartOutlined /> Body Composition Trend (Recent)</Space>} style={glassStyle}>
                                {metrics.length > 0 ? (
                                    <div style={{ height: 250 }}>
                                        <Bar data={bodyCompData} options={chartOptions} />
                                    </div>
                                ) : (
                                    <Empty description="No physical metrics logged yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                )}
                            </Card>

                            {/* Invoices */}
                            <Card 
                                title={<Space><FileTextOutlined /> Invoice History</Space>}
                                style={glassStyle}
                                extra={<Tooltip title="Refresh billing data"><Button icon={<HistoryOutlined />} type="text" onClick={fetchDashboardData} /></Tooltip>}
                            >
                                <Table 
                                    dataSource={invoices} 
                                    columns={invoiceColumns} 
                                    rowKey="inv_id"
                                    scroll={{ x: true }}
                                    pagination={{ pageSize: 5 }}
                                    expandable={{
                                        expandedRowRender: record => (
                                            <div style={{ padding: '16px 40px', backgroundColor: '#f9f9f9', borderRadius: 12 }}>
                                                <Title level={5}><DollarCircleOutlined /> Payment Breakdown</Title>
                                                {record.payments?.length > 0 ? (
                                                    <Table 
                                                        dataSource={record.payments} 
                                                        columns={paymentColumns} 
                                                        pagination={false} 
                                                        size="small" 
                                                        rowKey="inv_pay_id"
                                                    />
                                                ) : <Empty description="No payments made yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                                            </div>
                                        ),
                                    }}
                                />
                            </Card>
                        </Space>
                    </Col>
                </Row>
            </div>
        </Spin>
    );
};

export default MemberDashboard;
